/**
 * Loads the storefront's catalog fonts (`STOREFRONT_FONTS`, api-contract) on demand, for the
 * storefront page and the dashboard's theme preview alike.
 *
 * Each import is a `@fontsource-variable/*` package's CSS: Vite splits it, and the woff2 files its
 * `@font-face` rules name, into the importing app's own assets, so a font is fetched from the
 * page's origin, never a third party, and only by a page whose theme names it. Each file covers
 * one `unicode-range` subset, so the browser downloads only the subsets the page's text uses.
 */
import type { StorefrontFontId } from "@loot-os/api-contract";

/** One lazy import per catalog font. Keyed by the contract's ids, so a font added there fails here. */
export const STOREFRONT_FONT_LOADERS: Record<StorefrontFontId, () => Promise<unknown>> = {
  inter: () => import("@fontsource-variable/inter"),
  manrope: () => import("@fontsource-variable/manrope"),
  "dm-sans": () => import("@fontsource-variable/dm-sans"),
  "plus-jakarta-sans": () => import("@fontsource-variable/plus-jakarta-sans"),
  figtree: () => import("@fontsource-variable/figtree"),
  geist: () => import("@fontsource-variable/geist"),
  outfit: () => import("@fontsource-variable/outfit"),
  sora: () => import("@fontsource-variable/sora"),
  montserrat: () => import("@fontsource-variable/montserrat"),
  "space-grotesk": () => import("@fontsource-variable/space-grotesk"),
  "bricolage-grotesque": () => import("@fontsource-variable/bricolage-grotesque"),
  oswald: () => import("@fontsource-variable/oswald"),
  "playfair-display": () => import("@fontsource-variable/playfair-display"),
  fraunces: () => import("@fontsource-variable/fraunces"),
  lora: () => import("@fontsource-variable/lora"),
  "jetbrains-mono": () => import("@fontsource-variable/jetbrains-mono"),
};

/** Loads in flight or done, so each font is imported once per page. */
const requested = new Map<StorefrontFontId, Promise<void>>();

/**
 * Loads each named font's CSS once. Never rejects: a font that fails to load leaves its stack's
 * fallback on screen, which is no reason to break the page, and is retried on the next call.
 */
export function loadStorefrontFonts(ids: readonly StorefrontFontId[]): Promise<void> {
  return Promise.all(ids.map(loadStorefrontFont)).then(() => undefined);
}

function loadStorefrontFont(id: StorefrontFontId): Promise<void> {
  const pending = requested.get(id);
  if (pending) return pending;
  const loader = Object.hasOwn(STOREFRONT_FONT_LOADERS, id) ? STOREFRONT_FONT_LOADERS[id] : null;
  if (!loader) return Promise.resolve();

  const load = loader().then(
    () => undefined,
    (err: unknown) => {
      requested.delete(id);
      // biome-ignore lint/suspicious/noConsole: best-effort, and the page carries on in the fallback.
      console.warn(`storefront font "${id}" failed to load`, err);
    },
  );
  requested.set(id, load);
  return load;
}
